import {makeAutoObservable} from 'mobx';
import {ICheckboxesTreeItem} from '../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store';
import {getNullableChangesOrModel} from '../../../../models/model-types-methods';
import {uuid} from '../../../../services/helpers';
import {ISbrMarginGroup, ISbrMarginGroupSelection} from '../types';

interface ISbrMarginGroupChanges extends Partial<ISbrMarginGroup> {}

export default class SbrMarginGroupStore {
    public readonly clientId: string;
    private _model: ISbrMarginGroup;
    private _changes: ISbrMarginGroupChanges = {};
    private _deleted = false;
    private _selectedTreeItems?: ICheckboxesTreeItem[];

    constructor(
        model?: ISbrMarginGroup,
        private readonly shipmentOptions: ICheckboxesTreeItem[] = [],
    ) {
        this.clientId = uuid();
        this._model = model ?? {
            fixed_margin: null,
            relative_margin: null,
            selections: [],
        };
        if (this._model.selections?.length) {
            this._selectedTreeItems = this.selectionsToTreeItems(this._model.selections);
        }
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public deleted(): boolean {
        return this._deleted;
    }

    public setDeleted(value: boolean): void {
        this._deleted = value;
    }

    public getId(): number | undefined {
        return this._model.id;
    }

    public getFixedMargin(): number | null {
        return getNullableChangesOrModel(this._changes.fixed_margin, this._model.fixed_margin);
    }

    public getRelativeMargin(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.relative_margin, this._model.relative_margin);
    }

    public getSelections(): ISbrMarginGroupSelection[] {
        return this._changes.selections ?? this._model.selections ?? [];
    }

    public setFixedMargin(value: number | null): void {
        this._changes.fixed_margin = value;
    }

    public setRelativeMargin(value: number | null): void {
        this._changes.relative_margin = value;
    }

    public setSelections(selectedItems: ICheckboxesTreeItem[] | undefined): void {
        this._selectedTreeItems = selectedItems;
        this._changes.selections = this.treeItemsToSelections(selectedItems);
    }

    public getSelectedSelectionsTreeItems(): ICheckboxesTreeItem[] | undefined {
        if (!this._selectedTreeItems?.length) {
            return undefined;
        }

        return this._selectedTreeItems;
    }

    public hasIncompleteFields(): boolean {
        const hasFixed = this.getFixedMargin() !== null && this.getFixedMargin() !== undefined;
        const hasRelative = this.getRelativeMargin() !== null && this.getRelativeMargin() !== undefined;
        const hasSelections = this.getSelections().length > 0;
        const hasAny = hasFixed || hasRelative || hasSelections;
        const hasAll = hasFixed && hasRelative && hasSelections;

        return hasAny && !hasAll;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length || this._deleted;
    }

    public getUpdatedModel(): ISbrMarginGroup | null {
        if (this._deleted) {
            return null;
        }

        return {
            id: this._model.id,
            fixed_margin: this.getFixedMargin() ?? null,
            relative_margin: this.getRelativeMargin() ?? null,
            selections: this.getSelections().map(({carrier_code, service_code}) => ({
                carrier_code,
                service_code,
            })),
        };
    }

    private selectionsToTreeItems(selections: ISbrMarginGroupSelection[]): ICheckboxesTreeItem[] {
        const optionsByCarrierCode = new Map(this.shipmentOptions.map((carrier) => [carrier.value, carrier]));
        const carriersMap = new Map<string, ICheckboxesTreeItem>();

        selections.forEach((selection) => {
            const carrierOption = optionsByCarrierCode.get(selection.carrier_code);
            let carrierItem = carriersMap.get(selection.carrier_code);
            if (!carrierItem) {
                carrierItem = {
                    label: carrierOption?.label ?? selection.carrier_code,
                    value: selection.carrier_code,
                    childNodes: [],
                };
                carriersMap.set(selection.carrier_code, carrierItem);
            }

            const serviceOption = carrierOption?.childNodes?.find(
                (service) => service.value === selection.service_code
            );

            carrierItem.childNodes = [
                ...(carrierItem.childNodes ?? []),
                {
                    label: serviceOption?.label ?? selection.service_code,
                    value: selection.service_code,
                },
            ];
        });

        return Array.from(carriersMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    private treeItemsToSelections(selectedItems: ICheckboxesTreeItem[] | undefined): ISbrMarginGroupSelection[] {
        if (!selectedItems?.length) {
            return [];
        }

        const optionsByCarrierCode = new Map(this.shipmentOptions.map((carrier) => [carrier.value, carrier]));

        return selectedItems.flatMap((carrierItem) => {
            const carrierOption = optionsByCarrierCode.get(carrierItem.value);
            if (!carrierOption) {
                return [];
            }

            const selectedServiceItems = carrierItem.childNodes ?? [];

            if (!selectedServiceItems.length) {
                return (carrierOption.childNodes ?? []).map((service) => ({
                    carrier_code: carrierItem.value,
                    service_code: service.value,
                }));
            }

            return selectedServiceItems.map((serviceItem) => ({
                carrier_code: carrierItem.value,
                service_code: serviceItem.value,
            }));
        });
    }
}
